import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

/** A modal cart on top of Horizon's native add-to-cart pipeline. All amounts are rendered by Shopify Liquid. */
class MpCartDrawer extends Component {
  #abort; #queue = Promise.resolve(); #busy = false; #optedOut = new Set(); #addonWarnings = new Map();
  #deadline = 0; #timer; #previousFocus; #previousOverflow; #opened = false;
  get panel() { return this.querySelector('dialog'); }
  get isOpen() { return this.panel?.open === true; }
  get state() { return this.querySelector('.ac-state'); }
  get root() { return window.Shopify?.routes?.root || '/'; }

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    document.addEventListener('pointerdown', () => this.removeAttribute('data-ac-keyboard-focus'), options);
    document.addEventListener('keydown', event => {
      if (['Tab', 'Enter', ' '].includes(event.key)) this.setAttribute('data-ac-keyboard-focus', '');
    }, options);
    this.addEventListener('click', this.#click, options);
    this.addEventListener('change', this.#change, options);
    this.addEventListener('submit', this.#checkout, options);
    this.panel.addEventListener('cancel', event => { event.preventDefault(); if (this.querySelector('[data-ac-info][aria-expanded="true"]')) this.#hideInfo(); else this.close(); }, options);
    this.addEventListener('pointerover', event => { const button = event.target.closest('[data-ac-info]'); if (event.pointerType === 'mouse' && button) this.#showInfo(button); }, options);
    this.addEventListener('pointerout', event => { const wrap = event.target.closest('.ac-info-wrap'); if (event.pointerType === 'mouse' && wrap && !wrap.contains(event.relatedTarget)) this.#hideInfo(); }, options);
    this.addEventListener('focusin', event => { if (event.target.matches('[data-ac-info]:focus-visible')) this.#showInfo(event.target); }, options);
    this.addEventListener('focusout', event => { const wrap = event.target.closest('.ac-info-wrap'); if (wrap && !wrap.contains(event.relatedTarget)) this.#hideInfo(); }, options);
    this.panel.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const controls = [...this.panel.querySelectorAll('button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),[tabindex="0"]')].filter(e => e.getClientRects().length);
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && (document.activeElement === first || !this.panel.contains(document.activeElement))) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }, options);
    this.panel.addEventListener('click', event => {
      if (event.target !== this.panel) return;
      const rect = this.panel.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right) this.close();
    }, options);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#nativeUpdate, options);
    window.addEventListener('pageshow', event => { if (event.persisted && this.isOpen) this.#enqueue(() => this.#refresh()); }, options);
    window.addEventListener('focus', () => { if (this.isOpen) this.#enqueue(() => this.#refresh()); }, options);
    this.#timer = setInterval(() => this.#tick(), 1000);
  }
  disconnectedCallback() {
    super.disconnectedCallback(); this.#abort?.abort(); clearInterval(this.#timer);
    if (this.#opened) document.body.style.overflow = this.#previousOverflow || '';
  }
  toggle() { return this.isOpen ? this.close() : this.open(); }
  open() {
    if (this.isOpen) return this.#queue;
    this.#previousFocus = document.activeElement;
    this.#previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; this.#opened = true;
    this.panel.showModal(); this.setAttribute('open', '');
    this.#announceDrawer(true);
    this.querySelector('[data-ac-close]')?.focus({ preventScroll: true });
    return this.#enqueue(() => this.#refresh());
  }
  close() {
    if (!this.isOpen) return;
    this.#hideInfo();
    this.panel.close(); this.removeAttribute('open'); this.#opened = false;
    document.body.style.overflow = this.#previousOverflow || '';
    this.#announceDrawer(false);
    if (this.#previousFocus?.isConnected) this.#previousFocus.focus({ preventScroll: true });
  }
  #announceDrawer(open) {
    document.querySelectorAll('[aria-controls="cart-drawer"]').forEach(e => e.setAttribute('aria-expanded', String(open)));
  }
  #nativeUpdate = async event => {
    if (event.target === this) return;
    try {
      const result = event.promise ? await event.promise : null;
      if (result?.detail?.didError) return;
      if (event.action === 'add' && this.dataset.autoOpen !== 'false') {
        const modal = event.target instanceof Element ? event.target.closest('dialog[open]') : null;
        if (modal && modal !== this.panel && modal.matches(':modal')) {
          modal.addEventListener('close', () => this.open(), { once: true });
        } else if (!this.isOpen) {
          // Refresh before opening so the drawer never flashes the stale empty state.
          await this.#enqueue(() => this.#refresh());
          if (!this.isOpen) this.open();
        }
        else this.#enqueue(() => this.#refresh());
      } else if (this.isOpen) this.#enqueue(() => this.#refresh());
    } catch (error) { if (this.isOpen) this.#error(error); }
  };
  #enqueue(task) {
    const work = async () => {
      const focusKey = document.activeElement?.dataset?.focus;
      this.#busy = true; this.#setBusy(true); this.#error(null);
      try { await task(); }
      catch (error) { this.#error(error); }
      finally {
        this.#busy = false; this.#setBusy(false);
        if (this.isOpen && focusKey) {
          const focus = this.querySelector(`[data-focus="${CSS.escape(focusKey)}"]`) || this.querySelector('[data-ac-quantity]') || this.querySelector('[data-ac-close]');
          if (focus && !focus.disabled) focus.focus({ preventScroll: true });
        }
      }
    };
    this.#queue = this.#queue.then(work, work); return this.#queue;
  }
  #setBusy(busy) {
    this.panel.setAttribute('aria-busy', String(busy));
    this.querySelectorAll('.ac-content button,.ac-content input,.ac-content select').forEach(element => {
      if (busy) { if (!('wasDisabled' in element.dataset)) element.dataset.wasDisabled = String(element.disabled); element.disabled = true; }
      else if ('wasDisabled' in element.dataset) { element.disabled = element.dataset.wasDisabled === 'true'; delete element.dataset.wasDisabled; }
    });
  }
  #error(error) {
    const region = this.querySelector('[data-ac-error]');
    region.hidden = !error;
    region.textContent = error ? (error.message || 'The cart could not update. Please try again.') : '';
  }
  async #request(path, payload) {
    if (payload) {
      const url = new URL(window.location.href);
      url.searchParams.delete('sections'); url.searchParams.delete('section_id');
      payload = { ...payload, sections: ['mp-cart-content'], sections_url: url.pathname + url.search };
    }
    const response = await fetch(this.root + path, {
      method: payload ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { Accept: 'application/json', ...(payload ? { 'Content-Type': 'application/json' } : {}) },
      ...(payload ? { body: JSON.stringify(payload) } : {}), signal: AbortSignal.timeout(15000)
    });
    let data; try { data = await response.json(); } catch { throw new Error('The cart could not update. Please try again.'); }
    if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'The cart could not update. Please try again.');
    return data;
  }
  async #refresh(cart = null, sections = null) {
    cart ||= await this.#request('cart.js');
    sections ||= cart.sections;
    const config = this.state?.dataset || {};
    const addons = [...this.querySelectorAll('[data-ac-addon-config]')].map(e => ({
      key: e.dataset.acAddonConfig, id: Number(e.dataset.variant), product: Number(e.dataset.product),
      available: e.dataset.available === 'true', name: e.dataset.name
    })).filter(a => a.product && a.id);
    const addonIds = new Set(addons.map(a => a.product));
    const goods = cart.items.filter(item => !addonIds.has(item.product_id));
    if (!goods.length) {
      const extras = cart.items.filter(item => addonIds.has(item.product_id));
      if (extras.length) {
        cart = await this.#request('cart/update.js', { updates: Object.fromEntries(extras.map(item => [item.key, 0])) });
        sections = cart.sections;
      }
      this.#optedOut.clear(); this.#addonWarnings.clear(); this.#deadline = 0;
    } else {
      for (const addon of addons) {
        const lines = cart.items.filter(item => item.product_id === addon.product);
        if (!lines.length && addon.available && !this.#optedOut.has(addon.key)) {
          let added;
          try {
            added = await this.#request('cart/add.js', { items: [{ id: addon.id, quantity: 1 }] });
          } catch (error) {
            // Reconcile uncertain responses before deciding whether a charge exists.
            cart = await this.#request('cart.js'); sections = null;
            if (!cart.items.some(item => item.product_id === addon.product)) {
              this.#optedOut.add(addon.key);
              this.#addonWarnings.set(addon.key, `${addon.name} could not be added. You can check out without it, or check its box to retry.`);
            }
          }
          if (added) {
            sections = added.sections; cart = await this.#request('cart.js');
            this.#addonWarnings.delete(addon.key);
          }
        } else if (lines.length && (this.#optedOut.has(addon.key) || lines.length > 1 || lines[0].quantity !== 1)) {
          const updates = Object.fromEntries(lines.map((item, index) => [item.key, this.#optedOut.has(addon.key) || index > 0 ? 0 : 1]));
          cart = await this.#request('cart/update.js', { updates }); sections = cart.sections;
        }
      }
    }
    if (goods.length && !this.#deadline) this.#deadline = Date.now() + Number(config.duration || 300) * 1000;
    await this.#render(sections?.['mp-cart-content']);
    this.#publish(cart);
    this.#error(this.#addonWarnings.size ? new Error([...this.#addonWarnings.values()].join(' ')) : null);
  }
  async #render(html = null) {
    const body = this.querySelector('.ac-body'); const scroll = body?.scrollTop || 0;
    const selections = [...this.querySelectorAll('[data-ac-variant]')].map(select => [select.dataset.focus, select.value]);
    if (!html) {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id'); url.searchParams.set('sections', 'mp-cart-content');
      const response = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('The cart updated, but could not refresh. Please reopen it.');
      const sections = await response.json();
      html = sections['mp-cart-content'];
    }
    if (!html) throw new Error('The cart updated, but could not refresh. Please reopen it.');
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector('.ac-state');
    if (!parsed) throw new Error('The cart could not be displayed. Please reopen it.');
    this.querySelector('[data-ac-content]').replaceChildren(parsed);
    for (const [key, value] of selections) {
      const select = this.querySelector(`[data-focus="${CSS.escape(key)}"]`);
      if (select?.querySelector(`option[value="${CSS.escape(value)}"]:not(:disabled)`)) { select.value = value; this.#selectVariant(select); }
    }
    if (this.#busy) this.#setBusy(true);
    const nextBody = this.querySelector('.ac-body'); if (nextBody) nextBody.scrollTop = scroll;
    this.#tick();
  }
  #publish(cart) {
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines: [], promise: Promise.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { source: 'mp-cart', itemCount: cart.item_count, didError: false, items: cart.items } }) }));
    this.querySelector('[data-ac-status]').textContent = `Cart updated. ${cart.item_count} items.`;
  }
  #tick() {
    const timer = this.querySelector('[data-ac-countdown]'); if (!timer || !this.#deadline) return;
    // The reference restarts at 01:30 on expiry and leaves the cart intact.
    if (this.#deadline <= Date.now()) this.#deadline = Date.now() + 90000;
    const seconds = Math.ceil((this.#deadline - Date.now()) / 1000);
    timer.textContent = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
  }
  #hideInfo() {
    this.querySelectorAll('[data-ac-info]').forEach(button => { button.setAttribute('aria-expanded', 'false'); this.querySelector(`#${button.getAttribute('aria-controls')}`)?.setAttribute('hidden', ''); });
  }
  #showInfo(button) {
    this.#hideInfo(); button.setAttribute('aria-expanded', 'true');
    const tip = this.querySelector(`#${button.getAttribute('aria-controls')}`);
    if (tip) {
      tip.removeAttribute('hidden');
      const anchor = button.getBoundingClientRect(), bubble = tip.getBoundingClientRect();
      tip.style.setProperty('--ac-tip-arrow', `${Math.max(12, Math.min(bubble.width - 24, anchor.left + anchor.width / 2 - bubble.left - 6))}px`);
    }
  }
  #click = event => {
    const info = event.target.closest('[data-ac-info]');
    if (info) { if (info.getAttribute('aria-expanded') === 'true') this.#hideInfo(); else this.#showInfo(info); return; }
    if (!event.target.closest('.ac-info-wrap')) this.#hideInfo();
    const button = event.target.closest('button'); if (!button) return;
    if (button.hasAttribute('data-ac-close')) { this.close(); return; }
    if (this.#busy || button.disabled) return;
    if (button.hasAttribute('data-ac-add')) {
      this.#enqueue(async () => { const result = await this.#request('cart/add.js', { items: [{ id: Number(button.dataset.acAdd), quantity: 1 }] }); await this.#refresh(null, result.sections); });
    } else if (button.hasAttribute('data-ac-addon')) {
      const key = button.dataset.acAddon, next = button.getAttribute('aria-checked') === 'true';
      this.#enqueue(async () => {
        const previous = this.#optedOut.has(key);
        if (next) this.#optedOut.add(key); else this.#optedOut.delete(key);
        this.#addonWarnings.delete(key);
        try { await this.#refresh(); } catch (error) { if (previous) this.#optedOut.add(key); else this.#optedOut.delete(key); throw error; }
      });
    } else if (button.hasAttribute('data-ac-remove') || button.hasAttribute('data-ac-step')) {
      const line = button.closest('[data-key]'), input = line.querySelector('[data-ac-quantity]');
      const quantity = button.hasAttribute('data-ac-remove') ? 0 : Math.max(Number(input.min) || 1, Number(input.value) + Number(button.dataset.acStep) * (Number(input.step) || 1));
      this.#changeLine(line.dataset.key, quantity);
    }
  };
  #selectVariant(select) {
    const card = select.closest('[data-upsell]'), option = select.selectedOptions[0];
    if (!option) return;
    card.querySelector('[data-ac-add]').dataset.acAdd = option.value;
    card.querySelector('[data-ac-upsell-price]').textContent = option.dataset.price;
    const compare = card.querySelector('[data-ac-upsell-compare]');
    compare.textContent = option.dataset.compare || ''; compare.hidden = !option.dataset.compare;
  }
  #change = event => {
    const select = event.target.closest('[data-ac-variant]');
    if (select && !this.#busy) { this.#selectVariant(select); return; }
    const input = event.target.closest('[data-ac-quantity]'); if (!input || this.#busy) return;
    const quantity = Number(input.value), min = Number(input.min) || 1, step = Number(input.step) || 1;
    if (!Number.isSafeInteger(quantity) || quantity < 0 || (quantity !== 0 && (quantity < min || (quantity - min) % step))) {
      this.#error(new Error('Please enter a valid quantity.')); return;
    }
    this.#changeLine(input.closest('[data-key]').dataset.key, quantity);
  };
  #changeLine(key, quantity) {
    this.#enqueue(async () => { const cart = await this.#request('cart/change.js', { id: key, quantity }); await this.#refresh(cart); });
  }
  #checkout = event => {
    if (!event.target.matches('.ac-checkout-form')) return;
    event.preventDefault(); if (this.#busy) return;
    this.#enqueue(async () => {
      await this.#refresh();
      const form = this.querySelector('.ac-checkout-form'); if (!form) return;
      const input = document.createElement('input'); input.type = 'hidden'; input.name = 'checkout'; input.value = 'Check out'; form.append(input);
      HTMLFormElement.prototype.submit.call(form);
    });
  };
}
if (!customElements.get('mp-cart-drawer')) customElements.define('mp-cart-drawer', MpCartDrawer);
